import React, { useState, useEffect } from 'react'
import { Upload, Button, message, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { ICONS } from '../../utils/constants'
import Icon from '../Icon'
import ErrorList from '../ErrorList'
import styles from './styles.module.scss'

const MultipleImageUploader = ({ methods, formInputName, images = [], disabled = false }) => {
  const { t } = useTranslation()
  const { errors } = methods.formState
  const error = errors[formInputName]?.message

  const [imageList, setImageList] = useState([])
  const [selectedImageId, setSelectedImageId] = useState(null)
  const [defaultImageId, setDefaultImageId] = useState(null)

  useEffect(() => {
    methods.register(formInputName)
  }, [methods, formInputName])

  // Initialize from props
  useEffect(() => {
    if (images && images.length > 0) {
      // Only sync from props if imageList is empty (initial load) or if we need to sync
      if (imageList.length === 0) {
        const formattedImages = images?.filter(img=>!!(img.image?.src || img.src)).map((img) => ({
          id: img.image?.id || img.id, // Handle different structures
          src: img.image?.src || img.src,
          isDefault: img.image?.isDefault || false,
          file: null, // No file for existing images
        }))
        setImageList(formattedImages)
        
        const def = formattedImages.find((img) => img.isDefault)
        if (def) {
          setDefaultImageId(def.id)
          setSelectedImageId(def.id)
        } else {
           setSelectedImageId(formattedImages[0]?.id)
        }
      }
    } else if (images && images.length === 0 && imageList.length > 0) {
      // Reset state when images prop becomes empty (form reset scenario)
      setImageList([])
      setSelectedImageId(null)
      setDefaultImageId(null)
      updateForm([])
    }
  }, [images])

  // Set first valid image as default if no default is set
  useEffect(() => {
    const validImages = imageList.filter((img) => img.src && img.src.trim() !== '')
    if (validImages.length > 0 && !defaultImageId) {
      const firstValidImage = validImages[0]
      setDefaultImageId(firstValidImage.id)
      setSelectedImageId(firstValidImage.id)
      setImageList((prev) => {
        const newList = prev.map((img) => ({
          ...img,
          isDefault: img.id === firstValidImage.id,
        }))
        updateForm(newList)
        return newList
      })
    }
  }, [imageList, defaultImageId])

  const updateForm = (list) => {
     methods.setValue(formInputName, list, { shouldValidate: true, shouldDirty: true, shouldTouch: true })
  }

  const handleUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg'
    if (!isJpgOrPng) {
      message.error(t('You can only upload JPG/PNG file!'))
      return Upload.LIST_IGNORE
    }

    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const newId = file.uid
      const newImage = {
        id: newId,
        src: reader.result,
        file: file,
        isDefault: false,
      }

      setImageList((prev) => {
        const newList = [...prev, newImage]
        // If it's the first image, make it default and selected
        if (newList.length === 1) {
           newImage.isDefault = true
           setDefaultImageId(newId)
           setSelectedImageId(newId)
        } else if (!selectedImageId) {
           setSelectedImageId(newId)
        }
        updateForm(newList)
        return newList
      })
    }
    return false // Prevent upload
  }

  const handleDelete = (id) => {
    setImageList((prev) => {
      const newList = prev.filter((img) => img.id !== id)
      
      // Calculate new default/selected
      let newSelectedId = selectedImageId
      let newDefaultId = defaultImageId

      // If deleted image was selected, select first remaining or null
      if (id === selectedImageId) {
        newSelectedId = newList.length > 0 ? newList[0].id : null
      } else {
        // Ensure selectedImageId still exists in the new list
        const stillExists = newList.some(img => img.id === selectedImageId)
        if (!stillExists) {
          newSelectedId = newList.length > 0 ? newList[0].id : null
        }
      }
      
      if (id === defaultImageId) {
        if (newList.length > 0) {
           newList[0].isDefault = true
           newDefaultId = newList[0].id
        } else {
           newDefaultId = null
        }
      }
      
      // Reset both if list is empty
      if (newList.length === 0) {
        newSelectedId = null
        newDefaultId = null
      }
      
      setSelectedImageId(newSelectedId)
      setDefaultImageId(newDefaultId)
      updateForm(newList)
      return newList
    })
  }

  const handleSetDefault = (id) => {
    setDefaultImageId(id)
    setImageList((prev) => {
      const newList = prev.map((img) => ({
        ...img,
        isDefault: img.id === id,
      }))
      updateForm(newList)
      return newList
    })
  }
  
  const selectedImage = selectedImageId ? imageList.find((img) => img.id === selectedImageId) : null

  // Filter out images with empty src
  const validImages = imageList.filter((img) => img.src && img.src.trim() !== '')
  const hasValidImages = validImages.length > 0
  const selectedValidImage = selectedImage && selectedImage.src && selectedImage.src.trim() !== '' ? selectedImage : null

  return (
    <div className={'field'} style={{ width: '100%', display: 'block' }}>

      <div className={`${styles.container} ${!hasValidImages ? styles.emptyContainer : ''}`}>
        {hasValidImages && (
          <div className={styles.thumbnailsColumn}>
            <Upload
              multiple={true}
              showUploadList={false}
              beforeUpload={handleUpload}
              disabled={disabled}
              accept="image/jpeg,image/jpg,image/png"
            >
              <div className={styles.uploadButtonSmall}>
                <Icon icon={ICONS.PLUS} className={styles.uploadIcon} />
                <span>Add</span>
              </div>
            </Upload>

            {validImages.map((img) => {
              const isSelected = selectedImageId === img.id
              const isDefault = defaultImageId === img.id
              return (
                <div
                  key={img.id}
                  className={`${styles.thumbnailWrapper} ${isSelected ? styles.selected : ''}`}
                  onClick={() => setSelectedImageId(img.id)}
                >
                  <img src={img.src} alt="thumbnail" />
                  {isDefault && <div className={styles.defaultIndicator}>Default</div>}
                </div>
              )
            })}
          </div>
        )}

        <div className={styles.mainView}>
          <div className={`${styles.mainImageContainer} ${!selectedValidImage ? styles.emptyImageContainer : ''}`}>
            {selectedValidImage ? (
              <img src={selectedValidImage.src} alt="Preview" className={styles.mainImage} />
            ) : (
              <div className={styles.emptyState}>
                <Icon icon={ICONS.IMAGE} size={64} color="#d9d9d9" />
                <p>{t('No images uploaded')}</p>
                <Upload
                  multiple={true}
                  showUploadList={false}
                  beforeUpload={handleUpload}
                  disabled={disabled}
                  accept="image/jpeg,image/jpg,image/png"
                >
                  <Button type="primary" size="large" icon={<Icon icon={ICONS.UPLOAD} size={16} style={{ marginRight: 8 }} />}>
                    {t('Upload Images')}
                  </Button>
                </Upload>
              </div>
            )}
          </div>

          {selectedValidImage && (
            <div className={styles.controlsBar}>
              <div className={styles.statusSection}>
                <button
                  type="button"
                  className={`${styles.actionBtn} ${
                    defaultImageId === selectedValidImage.id ? styles.isDefault : styles.defaultBtn
                  }`}
                  onClick={() => {
                    if (defaultImageId !== selectedValidImage.id) {
                      handleSetDefault(selectedValidImage.id)
                    }
                  }}
                >
                  <Icon 
                    icon={ICONS.STAR} 
                    size={18} 
                    color={defaultImageId === selectedValidImage.id ? '#1890ff' : 'currentColor'}
                    style={{ marginRight: 0 }} 
                  />
                  <span>
                    {defaultImageId === selectedValidImage.id 
                      ? t('Default Image') 
                      : t('Set as Default')}
                  </span>
                </button>
              </div>

              <div className={styles.actions}>
                <Tooltip title={defaultImageId === selectedValidImage.id ? t("Cannot delete default image. Set another image as default first.") : t("Delete Image")}>
                  <div style={{ display: 'inline-block', cursor: defaultImageId === selectedValidImage.id ? 'not-allowed' : 'pointer' }}>
                    <button
                      type="button"
                      className={`${styles.actionBtn} ${styles.deleteBtn}`}
                    onClick={() => handleDelete(selectedValidImage.id)}
                      disabled={defaultImageId === selectedValidImage.id}
                      style={defaultImageId === selectedValidImage.id ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                    >
                      <Icon icon={ICONS.TRASH} size={18} color="currentColor" />
                      <span>{t('Delete')}</span>
                    </button>
                  </div>
                </Tooltip>
              </div>
            </div>
          )}
        </div>
      </div>

      {error ? <ErrorList errors={[error]} /> : null}
    </div>
  )
}

export default MultipleImageUploader
